import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { imgUrl } from '../utils/imgUrl';
import ProtectedImage from '../components/ProtectedImage';
import { annualIncomeRanges } from '../data/profileOptions';
import './ProfileDetail.css';

const ImageLightbox = ({ images, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);

  const prev = (e) => { e.stopPropagation(); setCurrent(i => (i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setCurrent(i => (i + 1) % images.length); };

  const handleKey = useCallback((e) => {
    if (e.key === 'ArrowLeft') setCurrent(i => (i - 1 + images.length) % images.length);
    if (e.key === 'ArrowRight') setCurrent(i => (i + 1) % images.length);
    if (e.key === 'Escape') onClose();
  }, [images.length, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>✕</button>
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        {images.length > 1 && (
          <button className="lightbox-nav lightbox-prev" onClick={prev}>‹</button>
        )}
        <img
          src={images[current]}
          alt={`Photo ${current + 1}`}
          className="lightbox-img"
          onContextMenu={e => e.preventDefault()}
          onDragStart={e => e.preventDefault()}
          style={{ pointerEvents: 'none' }}
        />
        {images.length > 1 && (
          <button className="lightbox-nav lightbox-next" onClick={next}>›</button>
        )}
      </div>
      {images.length > 1 && (
        <div className="lightbox-dots">
          {images.map((_, i) => (
            <span
              key={i}
              className={`lightbox-dot ${i === current ? 'active' : ''}`}
              onClick={e => { e.stopPropagation(); setCurrent(i); }}
            />
          ))}
        </div>
      )}
      <p className="lightbox-counter">{current + 1} / {images.length}</p>
    </div>
  );
};

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [interestStatus, setInterestStatus] = useState(null);
  const [interestLoading, setInterestLoading] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState({ unlocked: false, balance: 0 });
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    authService.getProfileById(id)
      .then(res => setProfile(res.data))
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
    authService.getInterestStatus(id).then(res => setInterestStatus(res.data.status));
    authService.getShortlistStatus(id).then(res => setShortlisted(res.data.shortlisted));
    authService.getContactStatus(id).then(res => {
      setContactStatus(res.data);
      if (res.data.unlocked) {
        authService.unlockContact(id).then(r => setContact(r.data));
      }
    });
  }, [id]);

  if (loading) return <div className="pd-loading">Loading profile...</div>;
  if (!profile) return null;

  const age = profile.date_of_birth
    ? Math.floor((new Date() - new Date(profile.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`;

  // Build images array from photos relation only, fallback to profile_photo
  const images = profile.photos?.length > 0
    ? profile.photos.map(p => imgUrl(p.photo_url))
    : (profile.profile_photo ? [imgUrl(profile.profile_photo)] : []);
  const hasImages = images.length > 0;

  const handleShortlist = async () => {
    try {
      const res = await authService.toggleShortlist(id);
      setShortlisted(res.data.shortlisted);
    } catch (err) { console.error(err); }
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await authService.unlockContact(id);
      setContact(res.data);
      setContactStatus(prev => ({ ...prev, unlocked: true, balance: prev.balance - 100 }));
    } catch (err) {
      if (err.response?.status === 402) {
        alert('Insufficient wallet balance. Please recharge your wallet.');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const maskPhone = (phone) => phone ? phone.slice(0, 4) + '******' : '**********';
  const maskEmail = (email) => email ? '********' + email.slice(8) : '********@*****.***';

  const handleInterest = async () => {
    setInterestLoading(true);
    try {
      const res = await authService.sendInterest(id);
      setInterestStatus(res.data.status);
    } catch (err) {
      console.error(err);
    } finally {
      setInterestLoading(false);
    }
  };

  const interestLabel = () => {
    if (interestLoading) return '...';
    if (interestStatus === 'pending') return '✓ Interest Sent';
    if (interestStatus === 'accepted') return '✅ Accepted';
    if (interestStatus === 'cancelled') return '💌 Send Interest';
    return '💌 Send Interest';
  };

  const Section = ({ title, children }) => (
    <div className="pd-section">
      <h3>{title}</h3>
      <div className="pd-section-grid">{children}</div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div className="pd-row">
      <span className="pd-label">{label}</span>
      <span className={`pd-value ${!value ? 'pd-value-empty' : ''}`}>{value || 'Not provided'}</span>
    </div>
  );

  return (
    <div className="pd-wrapper">
      {lightbox.open && (
        <ImageLightbox
          images={images}
          startIndex={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
        />
      )}

      {/* Header */}
      <div className="pd-header">
        <button className="pd-back" onClick={() => navigate('/dashboard')}>← Back to Matches</button>
      </div>

      {/* Hero */}
      <div className="pd-hero">
        <div className={`pd-avatar ${hasImages ? 'pd-avatar-clickable' : ''}`}
          onClick={() => hasImages && setLightbox({ open: true, index: 0 })}>
          {profile.profile_photo
            ? <>
                <ProtectedImage
                  src={imgUrl(profile.profile_photo)}
                  alt={profile.first_name}
                  style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
                />
                {images.length > 1 && <span className="pd-photo-count">{images.length} photos</span>}
              </>
            : <span>{initials}</span>
          }
        </div>
        <div className="pd-hero-info">
          <h1>{profile.first_name} {profile.last_name}</h1>
          <p className="pd-profile-id">{profile.profile_id}</p>
          <div className="pd-hero-tags">
            {age && <span>{age} years old</span>}
            {profile.height && <span>{profile.height} cm</span>}
            {profile.marital_status && <span>{profile.marital_status.replace('_', ' ')}</span>}
            {profile.mother_tongue && <span>{profile.mother_tongue}</span>}
            {profile.location?.city && <span>📍 {profile.location.city}, {profile.location.state}</span>}
          </div>
          <div className="pd-hero-actions">
            <button
              className={`pd-btn-interest ${interestStatus === 'pending' ? 'sent' : ''}`}
              onClick={handleInterest}
              disabled={interestLoading || interestStatus === 'accepted'}
            >
              {interestLabel()}
            </button>
            <button className="pd-btn-shortlist">♡ Shortlist</button>
            <button className="pd-btn-message">💬 Message</button>
          </div>
        </div>
      </div>

      <div className="pd-body">
        {/* Match Breakdown */}
        {profile.match_percentage !== undefined && profile.match_breakdown && Object.keys(profile.match_breakdown).length > 0 && (
          <div className="pd-section">
            <h3>Match Compatibility — {profile.match_percentage}%</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {Object.entries(profile.match_breakdown).map(([field, data]) => {
                const label = field.replace(/_/g, ' ');
                const preferred = Array.isArray(data.preferred)
                  ? data.preferred.join(', ')
                  : (typeof data.preferred === 'object' && data.preferred !== null)
                    ? `${data.preferred.from}–${data.preferred.to}`
                    : String(data.preferred ?? '');
                const actual = data.actual !== null && data.actual !== undefined ? String(data.actual) : 'Not set';
                return (
                  <div key={field} style={{
                    background: data.matched ? '#e8f5e9' : '#fce4ec',
                    padding: '6px 10px', borderRadius: 8, fontSize: '0.8rem',
                    border: `1px solid ${data.matched ? '#c8e6c9' : '#f8bbd0'}`,
                  }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{label}</span>
                    {data.matched
                      ? <span style={{ color: '#2e7d32' }}> ✅ {actual}</span>
                      : <span style={{ color: '#c62828' }}> ❌ {actual} <span style={{ color: '#888', fontSize: '0.7rem' }}>(want: {preferred})</span></span>
                    }
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contact Details */}
        <div className="pd-section pd-contact-section">
          <h3>Contact Details</h3>
          {contactStatus.unlocked && contact ? (
            <div className="pd-contact-unlocked">
              <div className="pd-contact-row">
                <span>📞 Phone</span>
                <strong>{contact.phone}</strong>
              </div>
              <div className="pd-contact-row">
                <span>✉️ Email</span>
                <strong>{contact.email}</strong>
              </div>
            </div>
          ) : (
            <div className="pd-contact-locked">
              <div className="pd-contact-row masked">
                <span>📞 Phone</span>
                <strong>{maskPhone(profile.user?.phone)}</strong>
              </div>
              <div className="pd-contact-row masked">
                <span>✉️ Email</span>
                <strong>{maskEmail(profile.user?.email)}</strong>
              </div>
              <div className="pd-unlock-box">
                <p>🔒 Unlock contact details for <strong>100 coins</strong></p>
                <p className="pd-wallet-bal">Your balance: <strong>🪙 {contactStatus.balance}</strong></p>
                {contactStatus.balance < 100 ? (
                  <button className="pd-btn-recharge" onClick={() => navigate('/wallet')}>Recharge Wallet</button>
                ) : (
                  <button className="pd-btn-unlock" onClick={handleUnlock} disabled={unlocking}>
                    {unlocking ? 'Unlocking...' : '🔓 Unlock for 100 coins'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* About */}
        {profile.about_me && (
          <div className="pd-section">
            <h3>About Me</h3>
            <p className="pd-about">{profile.about_me}</p>
          </div>
        )}

        {/* Basic Details */}
        <Section title="Basic Details">
          <Row label="Date of Birth" value={profile.date_of_birth} />
          <Row label="Age" value={age ? `${age} years` : null} />
          <Row label="Height" value={profile.height ? `${profile.height} cm` : null} />
          <Row label="Weight" value={profile.weight ? `${profile.weight} kg` : null} />
          <Row label="Marital Status" value={profile.marital_status?.replace('_', ' ')} />
          <Row label="Mother Tongue" value={profile.mother_tongue} />
          <Row label="Physical Status" value={profile.physical_status} />
          <Row label="Profile Created By" value={profile.profile_created_by} />
        </Section>

        {/* Religious Details */}
        <Section title="Religious Details">
          <Row label="Religion" value={profile.religious_details?.religion} />
          <Row label="Caste" value={profile.religious_details?.caste} />
          <Row label="Sub Caste" value={profile.religious_details?.sub_caste} />
          <Row label="Gotra" value={profile.religious_details?.gotra} />
          <Row label="Star (Nakshatra)" value={profile.religious_details?.star} />
          <Row label="Raasi" value={profile.religious_details?.raasi} />
        </Section>

        {/* Professional Details */}
        <Section title="Professional Details">
          <Row label="Occupation" value={profile.professional_details?.occupation} />
          <Row label="Category" value={profile.professional_details?.occupation_category?.replace(/_/g, ' ')} />
          <Row label="Company" value={profile.professional_details?.company_name} />
          <Row label="Annual Income" value={annualIncomeRanges.find(r => r.value === profile.professional_details?.annual_income)?.label || (profile.professional_details?.annual_income ? `₹${Number(profile.professional_details.annual_income).toLocaleString()}` : null)} />
          <Row label="Work Location" value={profile.professional_details?.work_location} />
        </Section>

        {/* Location */}
        <Section title="Location">
          <Row label="City" value={profile.location?.city} />
          <Row label="State" value={profile.location?.state} />
          <Row label="Country" value={profile.location?.country} />
        </Section>

        {/* Family Details */}
        <Section title="Family Details">
          <Row label="Father's Name" value={profile.family_details?.father_name} />
          <Row label="Father's Occupation" value={profile.family_details?.father_occupation} />
          <Row label="Mother's Name" value={profile.family_details?.mother_name} />
          <Row label="Mother's Occupation" value={profile.family_details?.mother_occupation} />
          <Row label="Siblings" value={profile.family_details?.siblings} />
          <Row label="Family Type" value={profile.family_details?.family_type} />
          <Row label="Family Status" value={profile.family_details?.family_status?.replace(/_/g, ' ')} />
          <Row label="Family Values" value={profile.family_details?.family_values} />
        </Section>

        {/* Lifestyle */}
        <Section title="Lifestyle">
          <Row label="Diet" value={profile.lifestyle?.diet} />
          <Row label="Smoking" value={profile.lifestyle?.smoking} />
          <Row label="Drinking" value={profile.lifestyle?.drinking} />
          <Row label="Hobbies" value={profile.lifestyle?.hobbies} />
          <Row label="Interests" value={profile.lifestyle?.interests} />
        </Section>

        {/* Kundli Matching */}
        {profile.kundli_matching && (
          <Section title="Kundli Matching">
            <Row label="Guna Milan Score" value={`${profile.kundli_matching.guna_milan?.total_points} / ${profile.kundli_matching.guna_milan?.maximum_points}`} />
            <Row label="Compatibility" value={profile.kundli_matching.message?.description} />
            <Row label="Girl Nakshatra" value={profile.kundli_matching.girl_info?.nakshatra?.name} />
            <Row label="Girl Rasi" value={profile.kundli_matching.girl_info?.rasi?.name} />
            <Row label="Boy Nakshatra" value={profile.kundli_matching.boy_info?.nakshatra?.name} />
            <Row label="Boy Rasi" value={profile.kundli_matching.boy_info?.rasi?.name} />
          </Section>
        )}
      </div>
    </div>
  );
};

export default ProfileDetail;
